import React, { useState, useEffect } from 'react';
import PageHeader from '../components/PageHeader';
import api from '../utils/api';
import { useAuth } from '../contexts/AuthContext';
import { useCurrency } from '../contexts/CurrencyContext';
import { useFiscalYear } from '../contexts/FiscalYearContext';
import { hasPermission } from '../utils/permissions';
import { formatDateDMY } from '../utils/dateFormat';
import DateInputDMY from '../components/DateInputDMY';
import SearchableSelect from '@/components/ui/SearchableSelect';
import DocumentViewModal from '../components/procurement/DocumentViewModal';
import DocumentPrintView from '../components/procurement/DocumentPrintView';
import FindDuplicatesButton from '../components/FindDuplicatesButton';
import { resolvePreparedBy, resolveApprovers } from '../utils/signatures';

const blankItem = () => ({
  product_id: '', product_name: '', part_number: '',
  quantity: 1, unit_price: 0,
  unit_price_bhd: 0, line_total_bhd: 0,
  landed_cost_share: 0, landed_unit_cost: 0,
  serial_numbers: [],
  warranty_months: 0,
});

const blankEntry = () => ({
  entry_number: '', entry_date: new Date().toISOString().split('T')[0],
  supplier_invoice_number: '',
  supplier_invoice_date: '',
  po_id: '', po_number: '',
  supplier_id: '', supplier_name: '',
  country: 'Bahrain', currency: 'BHD', exchange_rate: 1.0,
  items: [],
  freight_charge: 0,
  freight_charge_supplier_ccy: 0,  // user-entered in supplier currency; freight_charge (BHD) is derived
  clearance_charge: 0,
  customs_duty: 0, import_vat_amount: 0, other_charges: 0,
  items_total_bhd: 0, landed_overhead_total: 0, grand_total_bhd: 0,
  notes: '',
  vat_category: '',   // Empty = auto-classify on the VAT return
});

export default function PurchaseEntries() {
  const { user } = useAuth();
  const { companySettings } = useCurrency();
  const { selectedFy } = useFiscalYear();
  const [list, setList] = useState([]);
  const [pos, setPOs] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(blankEntry());
  const [q, setQ] = useState('');
  const [filterSupplierId, setFilterSupplierId] = useState('all');
  const [filterPONumber, setFilterPONumber] = useState('all');
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [viewing, setViewing] = useState(null);
  const [printing, setPrinting] = useState(null);

  // Sync global Fiscal Year selection → list date filter.
  useEffect(() => {
    if (!selectedFy) return;
    setFromDate(selectedFy.start_date);
    setToDate(selectedFy.end_date);
  }, [selectedFy?.id]);

  const canCreate = hasPermission(user, 'purchase_entries', 'create');
  const canEdit = hasPermission(user, 'purchase_entries', 'edit');
  const canDelete = hasPermission(user, 'purchase_entries', 'delete');

  const fetchAll = async () => {
    try {
      setLoading(true);
      const [pe, po, sup, usrs] = await Promise.all([
        api.get('/purchase-entries'),
        api.get('/purchase-orders'),
        api.get('/suppliers'),
        api.get('/users').catch(() => ({ data: [] })),
      ]);
      setList(pe.data || []);
      setPOs(po.data || []);
      setSuppliers(sup.data || []);
      setUsers(usrs.data || []);
    } catch (e) { console.error(e); } finally { setLoading(false); }
  };
  useEffect(() => { fetchAll(); }, []);

  const onPOChange = (poId) => {
    const po = pos.find((x) => x.id === poId);
    if (!po) return;
    // Exchange rate is captured at receipt time. Default to 1.0 for BHD; for
    // foreign currency the user must enter the actual rate paid.
    const rate = po.currency === 'BHD' ? 1.0 : 1.0;
    setForm({
      ...blankEntry(),
      entry_number: form.entry_number,  // keep what user already typed
      entry_date: form.entry_date,
      supplier_invoice_number: form.supplier_invoice_number,
      supplier_invoice_date: form.supplier_invoice_date,
      po_id: po.id,
      po_number: po.po_number,
      supplier_id: po.supplier_id,
      supplier_name: po.supplier_name,
      country: po.country,
      currency: po.currency,
      exchange_rate: rate,
      // Carry forward freight cost agreed on the PO. PO captures freight in
      // supplier currency — pre-fill the PE's supplier-currency freight field
      // and derive the BHD value via the exchange rate. User can still
      // override either value on the PE form.
      freight_charge_supplier_ccy: parseFloat(po.freight_charge) || 0,
      freight_charge: Number((((parseFloat(po.freight_charge) || 0) * rate)).toFixed(3)),
      items: (po.items || []).map((it) => {
        const qty = parseFloat(it.quantity) || 0;
        const up = parseFloat(it.unit_price) || 0;
        const upBhd = Number((up * rate).toFixed(3));
        return {
          product_id: it.product_id || '',
          product_name: it.product_name,
          part_number: it.part_number || '',
          po_quantity: qty,         // remember ordered qty for balance display
          quantity: qty,            // pre-fill received qty; user can reduce for partial receipts
          unit_price: up,
          unit_price_bhd: upBhd,
          line_total_bhd: Number((qty * upBhd).toFixed(3)),
          landed_cost_share: 0,
          landed_unit_cost: 0,
          serial_numbers: [],
          warranty_months: 0,
        };
      }),
    });
  };

  // Re-derive BHD freight from supplier-currency freight × exchange rate.
  // This is the single source of truth — landed-cost math uses freight_charge
  // (BHD) below, so we keep it in sync whenever the supplier-ccy amount or
  // exchange rate changes.
  useEffect(() => {
    const sup = parseFloat(form.freight_charge_supplier_ccy) || 0;
    const rate = parseFloat(form.exchange_rate) || 0;
    const bhd = Number((sup * rate).toFixed(3));
    if (bhd !== (parseFloat(form.freight_charge) || 0)) {
      setForm((prev) => ({ ...prev, freight_charge: bhd }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.freight_charge_supplier_ccy, form.exchange_rate]);

  // Recompute landed cost apportioning whenever overhead / lines change
  useEffect(() => {
    const items = form.items.map((it) => {
      const qty = parseFloat(it.quantity) || 0;
      const upBhd = parseFloat(it.unit_price_bhd) || 0;
      return { ...it, line_total_bhd: Number((qty * upBhd).toFixed(3)) };
    });
    const base = items.reduce((s, it) => s + it.line_total_bhd, 0);
    // Landed-cost overhead EXCLUDES VAT — VAT is a recoverable input tax kept
    // for VAT filing only. Only freight + clearance + customs + other go into
    // landed unit cost.
    const overhead = (parseFloat(form.freight_charge) || 0) + (parseFloat(form.clearance_charge) || 0) + (parseFloat(form.customs_duty) || 0) + (parseFloat(form.other_charges) || 0);
    const importVat = parseFloat(form.import_vat_amount) || 0;
    const enriched = items.map((it) => {
      const share = base > 0 ? (it.line_total_bhd / base) * overhead : (items.length ? overhead / items.length : 0);
      const qty = parseFloat(it.quantity) || 0;
      const landedUnit = qty > 0 ? (it.line_total_bhd + share) / qty : 0;
      return { ...it, landed_cost_share: Number(share.toFixed(3)), landed_unit_cost: Number(landedUnit.toFixed(3)) };
    });
    setForm((prev) => ({
      ...prev,
      items: enriched,
      items_total_bhd: Number(base.toFixed(3)),
      landed_overhead_total: Number(overhead.toFixed(3)),
      // Grand total still reflects what you paid (including VAT at customs)
      grand_total_bhd: Number((base + overhead + importVat).toFixed(3)),
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.freight_charge, form.clearance_charge, form.customs_duty, form.import_vat_amount, form.other_charges, JSON.stringify(form.items.map((i) => [i.quantity, i.unit_price_bhd]))]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.entry_number) { alert('Entry number required'); return; }
    if (!form.po_id) { alert('Pick a PO first'); return; }
    if (form.items.length === 0) { alert('No items to receive'); return; }
    try {
      // sanitise serial_numbers (string -> array) and strip UI-only fields
      const items = form.items.map((it) => {
        const sns = Array.isArray(it.serial_numbers) ? it.serial_numbers : String(it.serial_numbers || '').split(/[\n,]/).map((s) => s.trim()).filter(Boolean);
        // po_quantity is a UI-only helper for the balance column, not in the schema
        const { po_quantity, ...rest } = it;
        return { ...rest, serial_numbers: sns };
      });
      const payload = { ...form, items };
      if (editingId) await api.put(`/purchase-entries/${editingId}`, payload);
      else await api.post('/purchase-entries', payload);
      setShowForm(false);
      setEditingId(null);
      setForm(blankEntry());
      fetchAll();
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to save purchase entry');
    }
  };

  const handleEdit = (pe) => {
    setEditingId(pe.id);
    // Back-fill freight_charge_supplier_ccy for legacy PEs that only stored
    // the BHD freight. supplier_ccy ≈ BHD / exchange_rate.
    const rate = parseFloat(pe.exchange_rate) || 1;
    const supCcy = pe.freight_charge_supplier_ccy != null
      ? parseFloat(pe.freight_charge_supplier_ccy)
      : Number(((parseFloat(pe.freight_charge) || 0) / (rate || 1)).toFixed(3));
    setForm({
      ...blankEntry(),
      ...pe,
      entry_date: pe.entry_date?.split?.('T')[0] || pe.entry_date,
      freight_charge_supplier_ccy: supCcy,
    });
    setShowForm(true);
  };

  const handleDelete = async (pe) => {
    if (!window.confirm(`Delete entry "${pe.entry_number}"?`)) return;
    try { await api.delete(`/purchase-entries/${pe.id}`); fetchAll(); }
    catch (err) { alert(err.response?.data?.detail || 'Delete failed'); }
  };

  const updateLine = (idx, field, value) => {
    const items = [...form.items];
    items[idx] = { ...items[idx], [field]: value };
    setForm({ ...form, items });
  };

  const updateSerials = (idx, value) => {
    // store as raw string; we split on submit
    const items = [...form.items];
    items[idx] = { ...items[idx], serial_numbers: value };
    setForm({ ...form, items });
  };

  const filtered = list.filter((e) => {
    if (filterSupplierId !== 'all' && e.supplier_id !== filterSupplierId) return false;
    if (filterPONumber !== 'all' && (e.po_number || '') !== filterPONumber) return false;
    if (fromDate || toDate) {
      const d = e.entry_date ? new Date(e.entry_date) : null;
      if (!d) return false;
      if (fromDate && d < new Date(fromDate + 'T00:00:00')) return false;
      if (toDate && d > new Date(toDate + 'T23:59:59')) return false;
    }
    if (!q) return true;
    return (e.entry_number || '').toLowerCase().includes(q.toLowerCase())
        || (e.po_number || '').toLowerCase().includes(q.toLowerCase())
        || (e.supplier_name || '').toLowerCase().includes(q.toLowerCase());
  });
  const peSupplierIds = Array.from(new Set(list.map(e => e.supplier_id).filter(Boolean)));
  const pePOOptions = Array.from(new Set(list.map(e => e.po_number).filter(Boolean))).sort();

  return (
    <div className="p-4 space-y-4">
      <PageHeader title="Purchase Entries" subtitle="Record goods receipt against a PO — landed cost auto-apportioned" />

      <div className="flex flex-wrap gap-2 items-center">
        <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search entry / PO / supplier..." className="border px-3 py-2 rounded flex-1 min-w-[240px]" data-testid="pe-search" />
        {canCreate && (
          <button onClick={() => { setEditingId(null); setForm(blankEntry()); setShowForm(true); }} className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded font-medium" data-testid="add-pe-btn">+ New Purchase Entry</button>
        )}
        <FindDuplicatesButton
          module="purchase-entries"
          labelFields={['entry_number', 'supplier_name', 'entry_date', 'po_number', 'currency']}
          onAfterDelete={fetchAll}
        />
      </div>

      {/* Advanced filters */}
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-2" data-testid="pe-advanced-filters">
        <select value={filterSupplierId} onChange={(e) => setFilterSupplierId(e.target.value)} className="px-3 py-2 border rounded text-sm" data-testid="pe-filter-supplier">
          <option value="all">All suppliers</option>
          {suppliers.filter(s => peSupplierIds.includes(s.id)).map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
        </select>
        <select value={filterPONumber} onChange={(e) => setFilterPONumber(e.target.value)} className="px-3 py-2 border rounded text-sm" data-testid="pe-filter-po">
          <option value="all">All POs</option>
          {pePOOptions.map((p) => <option key={p} value={p}>{p}</option>)}
        </select>
        <DateInputDMY value={fromDate} onChange={(e) => setFromDate(e.target.value)} className="px-3 py-2 border rounded text-sm" placeholder="From date" data-testid="pe-filter-from-date" />
        <DateInputDMY value={toDate} onChange={(e) => setToDate(e.target.value)} className="px-3 py-2 border rounded text-sm" placeholder="To date" data-testid="pe-filter-to-date" />
        {(q || filterSupplierId !== 'all' || filterPONumber !== 'all' || fromDate || toDate) && (
          <button
            type="button"
            onClick={() => { setQ(''); setFilterSupplierId('all'); setFilterPONumber('all'); setFromDate(''); setToDate(''); }}
            className="px-3 py-2 border border-gray-300 rounded text-sm hover:bg-gray-50"
            data-testid="pe-clear-filters-btn"
          >
            Clear filters
          </button>
        )}
      </div>

      {/* Filtered totals (always in BHD — Purchase Entries are landed in BHD) */}
      {filtered.length > 0 && (() => {
        const t = filtered.reduce((acc, pe) => {
          acc.items += (pe.items_total_bhd || 0);
          acc.overhead += (pe.landed_overhead_total || 0);
          acc.grand += (pe.grand_total_bhd || 0);
          return acc;
        }, { items: 0, overhead: 0, grand: 0 });
        return (
          <div className="flex flex-wrap items-center gap-3 px-3 py-2 rounded-md bg-emerald-50 border border-emerald-200" data-testid="pe-filtered-totals">
            <span className="text-xs text-gray-700">Filtered: <b>{filtered.length}</b> entries</span>
            <span className="text-xs px-2 py-1 rounded bg-white border border-emerald-200" data-testid="pe-filtered-items-total">
              Items (BHD): <b>{t.items.toFixed(3)}</b>
            </span>
            <span className="text-xs px-2 py-1 rounded bg-white border border-emerald-200" data-testid="pe-filtered-overhead-total">
              Landed Overhead (BHD): <b>{t.overhead.toFixed(3)}</b>
            </span>
            <span className="text-xs px-2 py-1 rounded bg-emerald-100 border border-emerald-300 text-emerald-800" data-testid="pe-filtered-grand-total">
              Grand Total (BHD): <b>{t.grand.toFixed(3)}</b>
            </span>
          </div>
        );
      })()}

      <div className="overflow-x-auto bg-white rounded-lg shadow">
        <table className="w-full text-sm">
          <thead className="bg-gray-100">
            <tr>
              <th className="px-3 py-2 text-left">Entry #</th>
              <th className="px-3 py-2 text-left">Date</th>
              <th className="px-3 py-2 text-left">PO #</th>
              <th className="px-3 py-2 text-left">Supplier</th>
              <th className="px-3 py-2 text-right">Items (BHD)</th>
              <th className="px-3 py-2 text-right">Overhead (BHD)</th>
              <th className="px-3 py-2 text-right">Grand Total (BHD)</th>
              <th className="px-3 py-2 text-left">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y">
            {loading ? (
              <tr><td colSpan={8} className="text-center py-6 text-gray-500">Loading...</td></tr>
            ) : filtered.length === 0 ? (
              <tr><td colSpan={8} className="text-center py-6 text-gray-500">No purchase entries yet</td></tr>
            ) : filtered.map((pe) => (
              <tr key={pe.id} className="hover:bg-gray-50">
                <td className="px-3 py-2 font-medium text-blue-700">{pe.entry_number}</td>
                <td className="px-3 py-2">{formatDateDMY(pe.entry_date)}</td>
                <td className="px-3 py-2">{pe.po_number}</td>
                <td className="px-3 py-2">{pe.supplier_name}</td>
                <td className="px-3 py-2 text-right">{(pe.items_total_bhd || 0).toFixed(3)}</td>
                <td className="px-3 py-2 text-right">{(pe.landed_overhead_total || 0).toFixed(3)}</td>
                <td className="px-3 py-2 text-right font-semibold">{(pe.grand_total_bhd || 0).toFixed(3)}</td>
                <td className="px-3 py-2 whitespace-nowrap">
                  <button onClick={() => setViewing(pe)} className="text-gray-700 hover:text-gray-900 text-sm mr-3" data-testid={`view-pe-${pe.id}`}>👁️ View</button>
                  <button onClick={() => setPrinting(pe)} className="text-purple-600 hover:text-purple-800 text-sm mr-3" data-testid={`print-pe-${pe.id}`}>🖨 Print</button>
                  {canEdit && (
                    <button onClick={() => handleEdit(pe)} className="text-blue-600 hover:text-blue-800 text-sm mr-3">✏️ Edit</button>
                  )}
                  {canDelete && (
                    <button onClick={() => handleDelete(pe)} className="text-red-600 hover:text-red-800 text-sm">🗑 Delete</button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {showForm && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-start justify-center p-4 overflow-y-auto" data-testid="pe-form-modal">
          <div className="bg-white rounded-xl w-full max-w-6xl my-8">
            <div className="px-5 py-3 border-b flex items-center justify-between sticky top-0 bg-white rounded-t-xl">
              <h2 className="font-semibold text-lg">{editingId ? 'Edit Purchase Entry' : 'New Purchase Entry'}</h2>
              <button onClick={() => { setShowForm(false); setEditingId(null); }} className="text-gray-500 hover:text-gray-700">✕</button>
            </div>
            <form onSubmit={handleSubmit} className="p-5 space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                <div>
                  <label className="block text-sm mb-1">Entry Number *</label>
                  <input required value={form.entry_number} onChange={(e) => setForm({ ...form, entry_number: e.target.value })} className="w-full border rounded px-3 py-2" data-testid="pe-number-input" />
                </div>
                <div>
                  <label className="block text-sm mb-1">Entry Date *</label>
                  <DateInputDMY value={form.entry_date} onChange={(e) => setForm({ ...form, entry_date: e.target.value })} className="w-full border rounded px-3 py-2" />
                </div>
                <div>
                  <label className="block text-sm mb-1">Source PO *</label>
                  <SearchableSelect
                    required
                    value={form.po_id}
                    onChange={onPOChange}
                    options={pos
                      .filter((p) => p.status !== 'cancelled' && (p.approval_status || 'draft') === 'approved')
                      .map((p) => ({ value: p.id, label: `${p.po_number} · ${p.supplier_name} · ${p.country} · ${(p.total_amount || 0).toFixed(3)} ${p.currency}` }))}
                    placeholder="Select an approved PO to receive"
                  />
                  <p className="text-xs text-gray-500 mt-1">Only approved POs are listed. Draft or pending-approval POs are hidden.</p>
                </div>
                <div>
                  <label className="block text-sm mb-1">Supplier Invoice No</label>
                  <input
                    value={form.supplier_invoice_number || ''}
                    onChange={(e) => setForm({ ...form, supplier_invoice_number: e.target.value })}
                    className="w-full border rounded px-3 py-2"
                    placeholder="Supplier's tax invoice #"
                    data-testid="pe-supplier-invoice-number"
                  />
                </div>
                <div>
                  <label className="block text-sm mb-1">Supplier Invoice Date</label>
                  <DateInputDMY
                    value={form.supplier_invoice_date || ''}
                    onChange={(e) => setForm({ ...form, supplier_invoice_date: e.target.value })}
                    className="w-full border rounded px-3 py-2"
                    data-testid="pe-supplier-invoice-date"
                  />
                  <p className="text-xs text-gray-500 mt-1">Supplier warranty starts from this date.</p>
                </div>
              </div>

              {form.po_id && (
                <>
                  <div className="text-sm bg-blue-50 border border-blue-200 rounded px-3 py-2">
                    Supplier: <b>{form.supplier_name}</b> · Country: <b>{form.country}</b> · Currency: <b>{form.currency}</b>
                  </div>

                  {/* Exchange Rate is captured HERE (at goods receipt), not on the PO */}
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                    <div>
                      <label className="block text-sm mb-1">Exchange Rate ({form.currency} → BHD) *</label>
                      <input
                        type="number"
                        step="0.000001"
                        min="0"
                        value={form.exchange_rate}
                        disabled={form.currency === 'BHD'}
                        onChange={(e) => {
                          const rate = parseFloat(e.target.value) || 0;
                          // recompute each line's unit_price_bhd against the new rate
                          const items = form.items.map((it) => {
                            const upBhd = Number(((parseFloat(it.unit_price) || 0) * rate).toFixed(3));
                            return { ...it, unit_price_bhd: upBhd };
                          });
                          setForm({ ...form, exchange_rate: rate, items });
                        }}
                        className={`w-full border rounded px-3 py-2 ${form.currency === 'BHD' ? 'bg-gray-100' : ''}`}
                        data-testid="pe-exchange-rate"
                      />
                      <p className="text-xs text-gray-500 mt-1">
                        {form.currency === 'BHD'
                          ? 'BHD invoice — rate fixed at 1.0'
                          : 'Enter the actual rate paid for this receipt.'}
                      </p>
                    </div>
                  </div>

                  {/* Landed cost overhead */}
                  <div>
                    <h3 className="font-semibold mb-2">Landed Cost Components (BHD)</h3>
                    <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                      <div>
                        <label className="block text-xs mb-1">Freight ({form.currency})</label>
                        <input
                          type="number"
                          step="0.001"
                          value={form.freight_charge_supplier_ccy}
                          onChange={(e) => setForm({ ...form, freight_charge_supplier_ccy: parseFloat(e.target.value) || 0 })}
                          className="w-full border rounded px-2 py-1.5 text-sm text-right"
                          data-testid="pe-freight"
                        />
                        {form.currency !== 'BHD' && (
                          <p className="text-[10px] text-gray-500 mt-0.5 text-right" data-testid="pe-freight-bhd-caption">
                            ≈ {(parseFloat(form.freight_charge) || 0).toFixed(3)} BHD
                          </p>
                        )}
                      </div>
                      <div>
                        <label className="block text-xs mb-1">Clearance</label>
                        <input type="number" step="0.001" value={form.clearance_charge} onChange={(e) => setForm({ ...form, clearance_charge: parseFloat(e.target.value) || 0 })} className="w-full border rounded px-2 py-1.5 text-sm text-right" />
                      </div>
                      <div>
                        <label className="block text-xs mb-1">Customs Duty</label>
                        <input type="number" step="0.001" value={form.customs_duty} onChange={(e) => setForm({ ...form, customs_duty: parseFloat(e.target.value) || 0 })} className="w-full border rounded px-2 py-1.5 text-sm text-right" />
                      </div>
                      <div>
                        <label className="block text-xs mb-1" title="Recorded for VAT filing only — NOT added to landed unit cost.">
                          Import VAT <span className="text-gray-500">(VAT filing only)</span>
                        </label>
                        <input type="number" step="0.001" value={form.import_vat_amount} onChange={(e) => setForm({ ...form, import_vat_amount: parseFloat(e.target.value) || 0 })} className="w-full border rounded px-2 py-1.5 text-sm text-right" data-testid="pe-import-vat" />
                      </div>
                      <div>
                        <label className="block text-xs mb-1">Other</label>
                        <input type="number" step="0.001" value={form.other_charges} onChange={(e) => setForm({ ...form, other_charges: parseFloat(e.target.value) || 0 })} className="w-full border rounded px-2 py-1.5 text-sm text-right" />
                      </div>
                    </div>
                    <p className="text-xs text-gray-500 mt-1">Overhead is automatically apportioned to each line proportional to its BHD value.</p>
                  </div>

                  {/* Items */}
                  <div>
                    <h3 className="font-semibold mb-2">Items Received</h3>
                    <div className="overflow-x-auto">
                      <table className="w-full text-xs border">
                        <thead className="bg-gray-100">
                          <tr>
                            <th className="px-2 py-1 text-left">#</th>
                            <th className="px-2 py-1 text-left">Part #</th>
                            <th className="px-2 py-1 text-left">Product</th>
                            <th className="px-2 py-1 text-right">PO Qty</th>
                            <th className="px-2 py-1 text-right">Received</th>
                            <th className="px-2 py-1 text-right">Balance</th>
                            <th className="px-2 py-1 text-right">Unit ({form.currency})</th>
                            <th className="px-2 py-1 text-right">Unit (BHD)</th>
                            <th className="px-2 py-1 text-right">Line (BHD)</th>
                            <th className="px-2 py-1 text-right">+ Overhead</th>
                            <th className="px-2 py-1 text-right">Landed Unit Cost</th>
                            <th className="px-2 py-1 text-right">Warranty (mo)</th>
                            <th className="px-2 py-1 text-left">Serial Numbers (optional)</th>
                          </tr>
                        </thead>
                        <tbody>
                          {form.items.map((it, idx) => {
                            const poQty = parseFloat(it.po_quantity) || 0;
                            const recQty = parseFloat(it.quantity) || 0;
                            const balance = Math.max(0, poQty - recQty);
                            return (
                            <tr key={`pe-row-${idx}`} className="border-t">
                              <td className="px-2 py-1">{idx + 1}</td>
                              <td className="px-2 py-1">{it.part_number || '-'}</td>
                              <td className="px-2 py-1">{it.product_name}</td>
                              <td className="px-2 py-1 text-right text-gray-600">{poQty.toFixed(3)}</td>
                              <td className="px-2 py-1">
                                <input type="number" step="0.001" min="0" max={poQty || undefined}
                                  value={it.quantity}
                                  onChange={(e) => updateLine(idx, 'quantity', parseFloat(e.target.value) || 0)}
                                  className="w-20 border rounded px-1 py-0.5 text-right"
                                  data-testid={`pe-received-qty-${idx}`}
                                />
                              </td>
                              <td className={`px-2 py-1 text-right font-medium ${balance > 0 ? 'text-orange-600' : 'text-green-600'}`}>{balance.toFixed(3)}</td>
                              <td className="px-2 py-1"><input type="number" step="0.001" value={it.unit_price} onChange={(e) => {
                                const up = parseFloat(e.target.value) || 0;
                                const upBhd = Number((up * (form.exchange_rate || 1)).toFixed(3));
                                updateLine(idx, 'unit_price', up);
                                setTimeout(() => updateLine(idx, 'unit_price_bhd', upBhd), 0);
                              }} className="w-24 border rounded px-1 py-0.5 text-right" /></td>
                              <td className="px-2 py-1 text-right">{(it.unit_price_bhd || 0).toFixed(3)}</td>
                              <td className="px-2 py-1 text-right">{(it.line_total_bhd || 0).toFixed(3)}</td>
                              <td className="px-2 py-1 text-right text-amber-700">{(it.landed_cost_share || 0).toFixed(3)}</td>
                              <td className="px-2 py-1 text-right font-semibold text-emerald-700">{(it.landed_unit_cost || 0).toFixed(3)}</td>
                              <td className="px-2 py-1">
                                <input
                                  type="number"
                                  min="0"
                                  step="1"
                                  value={it.warranty_months || 0}
                                  onChange={(e) => updateLine(idx, 'warranty_months', parseInt(e.target.value) || 0)}
                                  className="w-16 border rounded px-1 py-0.5 text-right"
                                  data-testid={`pe-warranty-months-${idx}`}
                                />
                              </td>
                              <td className="px-2 py-1">
                                <textarea
                                  rows={1}
                                  placeholder="Comma or newline separated"
                                  value={Array.isArray(it.serial_numbers) ? it.serial_numbers.join(', ') : it.serial_numbers}
                                  onChange={(e) => updateSerials(idx, e.target.value)}
                                  className="w-full border rounded px-1 py-0.5"
                                />
                              </td>
                            </tr>
                          );})}
                        </tbody>
                      </table>
                    </div>
                    <p className="text-xs text-gray-500 mt-1">Serial numbers are optional. If you received less than PO quantity, the PO will be marked as <b>Partial</b> with the remaining balance.</p>
                  </div>

                  {/* Summary */}
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-3 text-sm">
                    <div className="bg-gray-50 p-3 rounded">
                      <div className="text-xs text-gray-500">Items Total (BHD)</div>
                      <div className="font-semibold text-lg">{(form.items_total_bhd || 0).toFixed(3)}</div>
                    </div>
                    <div className="bg-amber-50 p-3 rounded">
                      <div className="text-xs text-gray-500">Overheads (BHD)</div>
                      <div className="font-semibold text-lg">{(form.landed_overhead_total || 0).toFixed(3)}</div>
                    </div>
                    <div className="bg-emerald-50 p-3 rounded">
                      <div className="text-xs text-gray-500">Grand Total (BHD)</div>
                      <div className="font-bold text-xl text-emerald-700">{(form.grand_total_bhd || 0).toFixed(3)}</div>
                    </div>
                  </div>
                </>
              )}

              <div>
                <label className="block text-sm mb-1">Notes</label>
                <textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="w-full border rounded px-3 py-2" rows={2} />
              </div>

              {/* VAT Return Category override — empty = auto-classified on
                  the VAT Return (default rules: foreign + customs_vat > 0
                  → line 9, domestic → line 8). */}
              <div className="bg-amber-50 border border-amber-200 rounded p-2">
                <label className="block text-xs font-semibold text-amber-900 mb-1">
                  VAT Return Category (NBR classification)
                </label>
                <select
                  value={form.vat_category || ''}
                  onChange={(e) => setForm({ ...form, vat_category: e.target.value })}
                  className="w-full border border-amber-300 rounded px-2 py-1 text-sm bg-white"
                  data-testid="pe-vat-category"
                >
                  <option value="">Auto (based on country & customs VAT)</option>
                  <option value="standard_rated_domestic">Line 8 — Standard rated domestic purchases</option>
                  <option value="import_vat_at_customs">Line 9 — Imports — VAT paid at customs</option>
                  <option value="import_vat_deferral">Line 10 — Imports — VAT deferral</option>
                  <option value="import_reverse_charge">Line 11 — Imports — VAT via reverse charge (10%)</option>
                  <option value="purchase_reverse_charge">Line 12 — Domestic reverse charge</option>
                  <option value="non_registered_zero_rated">Line 13 — Non-registered / zero-rated / exempt</option>
                </select>
                <p className="text-xs text-amber-700 mt-1">
                  Leave on "Auto" unless this purchase belongs to a specific NBR category.
                </p>
              </div>

              <div className="flex justify-end gap-2 pt-2 border-t">
                <button type="button" onClick={() => { setShowForm(false); setEditingId(null); }} className="px-4 py-2 border rounded">Cancel</button>
                <button type="submit" className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded" data-testid="pe-save-btn">{editingId ? 'Save Changes' : 'Record Receipt'}</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* View modal */}
      {viewing && (() => {
        const sup = suppliers.find((s) => s.id === viewing.supplier_id) || {};
        const supplierLines = [
          viewing.supplier_name,
          [sup.address, viewing.country].filter(Boolean).join(', '),
          sup.phone ? `Tel: ${sup.phone}` : '',
          sup.email,
          sup.cr_number ? `CR No: ${sup.cr_number}` : '',
          sup.vat_number ? `VAT No: ${sup.vat_number}` : '',
        ].filter(Boolean);
        return (
        <DocumentViewModal
          title="Purchase Entry / Goods Receipt"
          docNumber={viewing.entry_number}
          docDate={viewing.entry_date}
          company={{
            name: companySettings?.name,
            address: companySettings?.address,
            phone: companySettings?.phone,
            email: companySettings?.email,
            tax_id: companySettings?.tax_id,
            cr_number: companySettings?.cr_number,
          }}
          parties={[
            { label: 'Supplier', lines: supplierLines },
            {
              label: 'Received By',
              lines: [
                companySettings?.name || 'Company',
                companySettings?.address,
                viewing.po_number ? `Against PO: ${viewing.po_number}` : '',
              ],
            },
          ]}
          meta={[
            { label: 'Currency', value: viewing.currency },
            { label: 'Exchange Rate', value: `${viewing.exchange_rate || 1} → BHD` },
            { label: 'Source PO', value: viewing.po_number },
            { label: 'Supplier Invoice No', value: viewing.supplier_invoice_number || '—' },
            { label: 'Supplier Invoice Date', value: viewing.supplier_invoice_date ? formatDateDMY(viewing.supplier_invoice_date) : '—' },
          ]}
          itemColumns={[
            { key: 'part_number', label: 'Part #' },
            { key: 'product_name', label: 'Product' },
            { key: 'quantity', label: 'Qty', align: 'right', numeric: true },
            { key: 'unit_price', label: `Unit (${viewing.currency})`, align: 'right', numeric: true },
            { key: 'unit_price_bhd', label: 'Unit (BHD)', align: 'right', numeric: true },
            { key: 'line_total_bhd', label: 'Line (BHD)', align: 'right', numeric: true },
            { key: 'landed_cost_share', label: '+ Overhead', align: 'right', numeric: true },
            { key: 'landed_unit_cost', label: 'Landed Unit Cost', align: 'right', numeric: true },
          ]}
          items={viewing.items || []}
          totals={[
            { label: 'Items Total (BHD)', value: (viewing.items_total_bhd || 0).toFixed(3) },
            { label: 'Freight', value: (viewing.freight_charge || 0).toFixed(3) },
            { label: 'Clearance', value: (viewing.clearance_charge || 0).toFixed(3) },
            { label: 'Customs Duty', value: (viewing.customs_duty || 0).toFixed(3) },
            { label: 'Import VAT', value: (viewing.import_vat_amount || 0).toFixed(3) },
            { label: 'Other', value: (viewing.other_charges || 0).toFixed(3) },
            { label: 'Overhead Total', value: (viewing.landed_overhead_total || 0).toFixed(3) },
            { label: 'Grand Total (BHD)', value: (viewing.grand_total_bhd || 0).toFixed(3), bold: true },
          ]}
          notes={viewing.notes}
          onClose={() => setViewing(null)}
          onPrint={() => { setPrinting(viewing); setViewing(null); }}
        />
        );
      })()}

      {/* Print preview */}
      {printing && (() => {
        const sup = suppliers.find((s) => s.id === printing.supplier_id) || {};
        const supplierLines = [
          printing.supplier_name,
          [sup.address, printing.country].filter(Boolean).join(', '),
          sup.phone ? `Tel: ${sup.phone}` : '',
          sup.email,
          sup.cr_number ? `CR No: ${sup.cr_number}` : '',
          sup.vat_number ? `VAT No: ${sup.vat_number}` : '',
        ].filter(Boolean);
        return (
        <DocumentPrintView
          title="Purchase Entry"
          docNumber={printing.entry_number}
          docDate={printing.entry_date}
          company={companySettings}
          parties={[
            { label: 'Supplier', lines: supplierLines },
            {
              label: 'Reference',
              lines: [
                printing.po_number ? `PO: ${printing.po_number}` : '',
                `Currency: ${printing.currency}`,
                printing.currency !== 'BHD' ? `Exchange Rate: ${printing.exchange_rate}` : '',
              ],
            },
          ]}
          meta={[]}
          itemColumns={[
            { key: 'part_number', label: 'Part #' },
            { key: 'product_name', label: 'Product' },
            { key: 'quantity', label: 'Qty', align: 'right', numeric: true },
            { key: 'unit_price_bhd', label: 'Unit (BHD)', align: 'right', numeric: true },
            { key: 'line_total_bhd', label: 'Line (BHD)', align: 'right', numeric: true },
            { key: 'landed_cost_share', label: '+ Overhead', align: 'right', numeric: true },
            { key: 'landed_unit_cost', label: 'Landed Unit Cost', align: 'right', numeric: true },
          ]}
          items={printing.items || []}
          totals={[
            { label: 'Items Total', value: `${(printing.items_total_bhd || 0).toFixed(3)} BHD` },
            { label: 'Overheads', value: `${(printing.landed_overhead_total || 0).toFixed(3)} BHD` },
            { label: 'Grand Total', value: `${(printing.grand_total_bhd || 0).toFixed(3)} BHD`, bold: true },
          ]}
          notes={printing.notes}
          preparedBy={resolvePreparedBy(printing, users)}
          approvers={resolveApprovers(printing, users)}
          onClose={() => setPrinting(null)}
        />
        );
      })()}
    </div>
  );
}
